import { create } from "zustand";
import { useToastStore } from "@/shared/stores/toastStore";
import { ApiTable } from "@/shared/api/table/api-table";
import { getOrderPrice } from "@/shared/api/orders/api-orders";
import type { TableStatusInfo } from "@/modules/ServiceCore/components/ServiceStatus";
import type { Tariff, UserProfile } from "@/shared/api/types";
import { useServiceWorkflowStore } from "./serviceWorkflowStore";

interface TableStatusApiResponse {
  id: string;
  user_id: string;
  supplier_id: string;
  table_type: string;
  subscription_type: "free" | "paid" | "disabled";
  created_at: string;
  expires_at: string;
  price?: number;
  table_url?: string;
}

interface TableStatusState {
  tableStatus: TableStatusInfo | null;
  isLoading: boolean;
  error: string | null;
  fetchTableStatus: (
    tableId: string,
    tariff: Tariff | null | undefined,
    profile: UserProfile | null,
  ) => Promise<void>;
  reset: () => void;
}

const initialState = {
  tableStatus: null,
  isLoading: true,
  error: null,
};

export const useTableStatusStore = create<TableStatusState>((set) => ({
  ...initialState,
  reset: () => set(initialState),
  fetchTableStatus: async (tableId, tariff, profile) => {
    const { serviceType } = useServiceWorkflowStore.getState();
    const { showToast } = useToastStore.getState();

    if (!tariff || !tableId || !profile) {
      set({ isLoading: false, tableStatus: null });
      return;
    }

    set({ isLoading: true, error: null });

    const tableApi = new ApiTable();
    let response;
    try {
      response = await tableApi.getTableStatus(tableId);
    } catch (err) {
      console.log(err);
      showToast("Таблица не найдена", "error");
      set({ error: "Таблица не найдена", isLoading: false });
      return;
    }

    const apiData = response.data as TableStatusApiResponse;
    let price: string;

    if (apiData.subscription_type === "free" && serviceType === "auto_jam") {
      price = "Бесплатно";
    } else if (serviceType) {
      try {
        const priceData = await getOrderPrice({
          tariff_id: tariff.id,
          service_type: serviceType,
          service_id: tableId,
        });
        price = `${priceData.price} ₽\n/ мес.`;
      } catch {
        showToast("Не удалось получить данные о цене", "error");
        price = "Ошибка загрузки";
      }
    } else {
      price = "Бесплатно";
    }

    const isExpired = apiData.expires_at
      ? new Date(apiData.expires_at) < new Date()
      : false;

    const futureTimeDiff =
      new Date(apiData.expires_at).getTime() - new Date().getTime();
    const futureHoursDiff = futureTimeDiff / (1000 * 60 * 60);
    const isExpiringSoon = futureHoursDiff > 0 && futureHoursDiff <= 24;

    const mappedStatus: TableStatusInfo = {
      status:
        apiData.subscription_type === "paid" ||
        apiData.subscription_type === "free"
          ? "Активный"
          : "Неактивный",
      subscriptionType: apiData.subscription_type,
      price,
      expiresAt: apiData.expires_at
        ? new Date(apiData.expires_at).toLocaleDateString("ru-RU")
        : "—",
      tableUrl: `https://docs.google.com/spreadsheets/d/${apiData.id}/`,
      tableId: apiData.id,
      isOwner: apiData.user_id === profile.id,
      isExpired,
      isExpiringSoon,
    };

    set({ tableStatus: mappedStatus, isLoading: false });
  },
}));
